import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import socket from "../../utils/socket";
import { toast } from "../../utils/toast";
import { ArrowLeft, Monitor, Square, Circle, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import "./ScreenViewer.css";

// How long to wait for first video frame before showing "retry" UI.
const STREAM_CONNECT_TIMEOUT_MS = 15_000;

// How long to wait in "disconnected" before attempting ICE restart.
const ICE_RESTART_DELAY_MS = 4_000;

// Maximum auto-retry attempts before requiring manual intervention.
const MAX_AUTO_RETRIES = 3;

function buildIceServers() {
  const stun = import.meta.env.VITE_STUN_URL || "stun:stun.l.google.com:19302";
  const turnUrl = import.meta.env.VITE_TURN_URL;
  const turnUsername = import.meta.env.VITE_TURN_USERNAME || "";
  const turnCredential = import.meta.env.VITE_TURN_CREDENTIAL || "";

  // Multiple STUN servers — if one is blocked (corporate network), others fill in.
  const servers = [
    { urls: stun },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
  ];

  if (turnUrl) {
    const turnUrls = Array.isArray(turnUrl) ? [...turnUrl] : [turnUrl];
    // TCP TURN — works through firewalls that block UDP.
    const primary = turnUrls[0];
    if (primary && !primary.includes("?transport=")) {
      turnUrls.push(primary + "?transport=tcp");
    }
    servers.push({
      urls: turnUrls,
      username: turnUsername,
      credential: turnCredential,
    });
  }
  return servers;
}

function formatDuration(totalSec) {
  const sec = Math.max(0, totalSec || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

export default function ScreenViewer() {
  const { id: agentId } = useParams();
  const navigate = useNavigate();

  const [showStartScreen, setShowStartScreen] = useState(true);
  const [stealthMode, setStealthMode] = useState(false);
  const [status, setStatus] = useState("Requesting session...");
  const [isLive, setIsLive] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [canRetry, setCanRetry] = useState(false);

  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const sessionIdRef = useRef(null);
  const endRequestedRef = useRef(false);
  const pendingRemoteCandidatesRef = useRef([]);
  const remoteDescriptionSetRef = useRef(false);
  const connectTimeoutRef = useRef(null);
  const iceRestartTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const isLiveRef = useRef(false);
  // Tracks whether the current session setup is still active (guards async paths).
  const activeRef = useRef(false);

  const timerLabel = useMemo(() => formatDuration(elapsedSec), [elapsedSec]);

  const clearMedia = () => {
    try {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks?.().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }
    } catch {
      // best effort
    }
  };

  const closePeer = () => {
    if (iceRestartTimerRef.current) {
      clearTimeout(iceRestartTimerRef.current);
      iceRestartTimerRef.current = null;
    }
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    try {
      if (pcRef.current) {
        pcRef.current.ontrack = null;
        pcRef.current.onicecandidate = null;
        pcRef.current.onconnectionstatechange = null;
        pcRef.current.oniceconnectionstatechange = null;
        pcRef.current.close();
      }
    } catch {
      // best effort
    }
    pcRef.current = null;
  };

  const cleanupLocal = () => {
    closePeer();
    clearMedia();
    isLiveRef.current = false;
    setIsLive(false);
  };

  const armConnectTimeout = () => {
    if (connectTimeoutRef.current) clearTimeout(connectTimeoutRef.current);
    connectTimeoutRef.current = setTimeout(() => {
      if (isLiveRef.current || !activeRef.current) return;
      // Stream never arrived — surface retry UI
      setStatus("Stream not received. Check agent connection.");
      setCanRetry(true);
    }, STREAM_CONNECT_TIMEOUT_MS);
  };

  const createPeerConnection = () => {
    closePeer();

    const pc = new RTCPeerConnection({
      iceServers: buildIceServers(),
      iceTransportPolicy: "all",   // must be in constructor config — setting afterwards has no effect
      bundlePolicy: "max-bundle",
      rtcpMuxPolicy: "require",
    });

    pc.ontrack = (event) => {
      if (!activeRef.current) return;
      const [stream] = event.streams || [];
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
      }
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      isLiveRef.current = true;
      retryCountRef.current = 0;
      setRetryCount(0);
      setCanRetry(false);
      setStatus("Live stream connected");
      setIsLive(true);
    };

    pc.onicecandidate = (event) => {
      if (!event.candidate || !sessionIdRef.current || !activeRef.current) return;
      socket.emit("screen:ice_candidate", {
        sessionId: sessionIdRef.current,
        candidate: event.candidate,
      });
    };

    pc.onconnectionstatechange = () => {
      if (!activeRef.current) return;
      const state = pc.connectionState;
      console.log("[ScreenViewer] connection state →", state);

      if (state === "connected") {
        if (iceRestartTimerRef.current) {
          clearTimeout(iceRestartTimerRef.current);
          iceRestartTimerRef.current = null;
        }
        setStatus("Live stream connected");
      } else if (state === "disconnected") {
        // "disconnected" is transient — attempt ICE restart first before giving up.
        setStatus("Connection blip — attempting ICE restart...");
        if (iceRestartTimerRef.current) clearTimeout(iceRestartTimerRef.current);
        iceRestartTimerRef.current = setTimeout(async () => {
          if (!activeRef.current || !pcRef.current) return;
          try {
            // restartIce() forces a new ICE gathering round on the same connection.
            pcRef.current.restartIce();
            console.log("[ScreenViewer] ICE restart triggered");
            setStatus("ICE restart in progress...");
          } catch (err) {
            console.warn("[ScreenViewer] ICE restart failed:", err);
            handleFailed();
          }
        }, ICE_RESTART_DELAY_MS);
      } else if (state === "failed") {
        if (iceRestartTimerRef.current) {
          clearTimeout(iceRestartTimerRef.current);
          iceRestartTimerRef.current = null;
        }
        handleFailed();
      } else if (state === "closed") {
        setStatus("Connection closed");
        setIsLive(false);
        isLiveRef.current = false;
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("[ScreenViewer] ICE connection state →", pc.iceConnectionState);
    };

    pcRef.current = pc;
    return pc;
  };

  const handleFailed = () => {
    const attempts = retryCountRef.current;
    if (attempts < MAX_AUTO_RETRIES && activeRef.current) {
      retryCountRef.current += 1;
      setRetryCount(retryCountRef.current);
      setStatus(`Connection failed — auto-retrying (${retryCountRef.current}/${MAX_AUTO_RETRIES})...`);
      setIsLive(false);
      isLiveRef.current = false;
      // Re-emit the view request which will trigger a new offer from the agent.
      setTimeout(() => {
        if (!activeRef.current) return;
        cleanupLocal();
        pendingRemoteCandidatesRef.current = [];
        remoteDescriptionSetRef.current = false;
        setStatus("Re-requesting session...");
        armConnectTimeout();
        socket.emit("screen:view_request", { agentId, stealth: stealthMode, sessionId: sessionIdRef.current }, () => {});
      }, 1500);
    } else {
      setStatus("Connection failed. Click retry to try again.");
      setIsLive(false);
      isLiveRef.current = false;
      setCanRetry(true);
    }
  };

  const handleManualRetry = () => {
    if (!activeRef.current) return;
    retryCountRef.current = 0;
    setRetryCount(0);
    setCanRetry(false);
    cleanupLocal();
    pendingRemoteCandidatesRef.current = [];
    remoteDescriptionSetRef.current = false;
    setStatus("Re-requesting session...");
    armConnectTimeout();
    socket.emit("screen:view_request", { agentId, stealth: stealthMode, sessionId: sessionIdRef.current }, (resp) => {
      if (!activeRef.current) return;
      if (!resp?.success) {
        setStatus(resp?.message || "Screen request failed");
        setCanRetry(true);
      }
    });
  };

  useEffect(() => {
    if (showStartScreen) return;

    activeRef.current = true;
    retryCountRef.current = retryCount;

    const onOffer = async ({ sessionId, sdp }) => {
      try {
        if (!activeRef.current) return;
        if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
        sessionIdRef.current = sessionId;

        const pc = createPeerConnection();
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
        remoteDescriptionSetRef.current = true;

        while (pendingRemoteCandidatesRef.current.length > 0) {
          const queued = pendingRemoteCandidatesRef.current.shift();
          try {
            await pc.addIceCandidate(new RTCIceCandidate(queued));
          } catch (iceErr) {
            console.warn("[ScreenViewer] Queued ICE apply failed", iceErr);
          }
        }

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        socket.emit("screen:answer", { sessionId, sdp: answer });
        setStatus("Negotiating secure peer connection...");
      } catch (err) {
        console.error("[ScreenViewer] offer handling failed", err);
        toast.error("Failed to process stream offer");
        setStatus("Offer processing failed");
        setCanRetry(true);
      }
    };

    const onIceCandidate = async ({ sessionId, candidate }) => {
      try {
        if (!pcRef.current || !activeRef.current) return;
        if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
        if (!remoteDescriptionSetRef.current) {
          pendingRemoteCandidatesRef.current.push(candidate);
          return;
        }
        await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.warn("[ScreenViewer] ICE candidate apply failed", err);
      }
    };

    const onSessionEnded = ({ sessionId, endReason }) => {
      if (sessionIdRef.current && sessionId !== sessionIdRef.current) return;
      setStatus(endReason ? `Session ended: ${endReason}` : "Session ended");
      cleanupLocal();
    };

    socket.on("screen:offer", onOffer);
    socket.on("screen:ice_candidate", onIceCandidate);
    socket.on("screen:session_ended", onSessionEnded);

    armConnectTimeout();

    socket.emit("screen:view_request", { agentId, stealth: stealthMode }, (resp) => {
      if (!activeRef.current) return;
      if (!resp?.success) {
        setStatus(resp?.message || "Screen request failed");
        toast.error(resp?.message || "Unable to start screen session");
        setCanRetry(true);
        return;
      }

      sessionIdRef.current = resp.sessionId;
      const start = resp.startTime ? new Date(resp.startTime) : new Date();
      setStartedAt(start);
      setStatus("Session created. Waiting for agent stream...");
    });

    return () => {
      activeRef.current = false;
      socket.off("screen:offer", onOffer);
      socket.off("screen:ice_candidate", onIceCandidate);
      socket.off("screen:session_ended", onSessionEnded);

      if (sessionIdRef.current && !endRequestedRef.current) {
        socket.emit("screen:end", {
          sessionId: sessionIdRef.current,
          reason: "viewer_unmounted",
        });
      }

      cleanupLocal();
      pendingRemoteCandidatesRef.current = [];
      remoteDescriptionSetRef.current = false;
    };
  }, [agentId, showStartScreen]);

  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        const el = videoRef.current?.parentElement || videoRef.current;
        await el?.requestFullscreen();
        try { await screen.orientation.lock("landscape"); } catch { /* not supported */ }
      } else {
        await document.exitFullscreen();
        try { screen.orientation.unlock(); } catch { /* best effort */ }
      }
    } catch {
      setIsFullscreen((v) => !v);
    }
  };

  const handleEndSession = () => {
    if (!sessionIdRef.current) {
      navigate("/devices");
      return;
    }
    endRequestedRef.current = true;
    socket.emit("screen:end", { sessionId: sessionIdRef.current, reason: "admin_end" }, (resp) => {
      if (!resp?.success) {
        toast.error(resp?.message || "Failed to end session");
        return;
      }
      cleanupLocal();
      navigate("/devices");
    });
  };

  if (showStartScreen) {
    return (
      <div className="sv-wrapper sv-start-wrapper">
        <div className="sv-start-card">
          <Monitor size={38} className="sv-start-icon" />
          <h2 className="sv-start-title">Start Screen Mirroring</h2>
          <p className="sv-start-agent">
            Agent: <strong>{agentId}</strong>
          </p>

          <div className="sv-stealth-row">
            <div className="sv-stealth-info">
              <span className="sv-stealth-label">Stealth Mode</span>
              <span className="sv-stealth-desc">
                User won&apos;t see the monitoring notification
              </span>
            </div>
            <button
              className={`sv-stealth-toggle ${stealthMode ? "on" : "off"}`}
              onClick={() => setStealthMode((v) => !v)}
              title={stealthMode ? "Stealth ON" : "Stealth OFF"}
            >
              <span className="sv-toggle-thumb" />
            </button>
          </div>

          <div className="sv-start-actions">
            <button className="sv-btn ghost" onClick={() => navigate("/devices")}>
              Cancel
            </button>
            <button
              className="sv-btn sv-btn-start"
              onClick={() => setShowStartScreen(false)}
            >
              <Monitor size={15} /> Start Mirroring
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`sv-wrapper${isFullscreen ? " sv-wrapper--fullscreen" : ""}`}>
      {isFullscreen && (
        <button className="sv-btn ghost sv-fullscreen-exit" onClick={toggleFullscreen} title="Exit fullscreen">
          <Minimize2 size={18} />
        </button>
      )}
      <div className="sv-header">
        <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices">
          <ArrowLeft size={16} />
        </button>

        <div className="sv-meta">
          <div className="sv-title">
            <Monitor size={18} /> Live Screen View
          </div>
          <div className="sv-subtitle">Agent: {agentId}</div>
        </div>

        <div className="sv-right">
          {retryCount > 0 && (
            <div className="sv-retry-badge">Retry {retryCount}/{MAX_AUTO_RETRIES}</div>
          )}
          {!stealthMode && (
            <div className="sv-visible-badge" title="The user can see a notification: &#39;Your screen is being viewed by Administrator&#39;">
              ⚠ User is notified
            </div>
          )}
          <div className="sv-live">
            <Circle size={12} className={isLive ? "dot live" : "dot"} />
            {isLive ? "LIVE" : "CONNECTING"}
          </div>
          <div className="sv-timer">{timerLabel}</div>
          <button className="sv-btn ghost" onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button className="sv-btn danger" onClick={handleEndSession}>
            <Square size={14} /> End Session
          </button>
        </div>
      </div>

      <div className="sv-stage">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={false}
          className="sv-video"
        />
        <div className="sv-status">{status}</div>
        {canRetry && (
          <button className="sv-btn sv-retry-btn" onClick={handleManualRetry}>
            <RefreshCw size={14} /> Retry Connection
          </button>
        )}
      </div>
    </div>
  );
}
